// Готовая задача владельца: краткий итог простыми словами, кто сделал, когда; открыть или отметить просмотренной.
// Коллеги написали в задаче Битрикс24 уже после закрытия — пометка с последним сообщением и кнопка «Отдать агенту».
import { api } from '../api.ts'
import { useBusy } from '../lib/hooks.ts'
import { outcomeText } from '../lib/outcome.ts'
import { timeAgo } from '../lib/format.ts'
import { TaskLink, openTask } from '../router.tsx'
import { useAgentMap, useNow } from '../store.ts'
import { toast, toastError } from '../toast.ts'
import type { TaskCard } from '../types.ts'
import { Button, cx } from './ui.tsx'

export function DoneCard({ card, onSeen, compact }: { card: TaskCard; onSeen?: () => void; compact?: boolean }) {
  const now = useNow()
  const agents = useAgentMap()
  const who = card.assignee_agent_id ? (agents.get(card.assignee_agent_id)?.name ?? card.assignee_agent_id) : null
  const { text, brief } = outcomeText(card)
  const [busy, run] = useBusy()
  const news = card.bitrix_news
  const handBack = () =>
    run(async () => {
      try {
        await api.post(`/api/tasks/${card.id}/comment`, {
          body: 'После закрытия в задаче Битрикс24 появились новые сообщения коллег (они в переписке задачи). Разберись: если нужна работа — сделай её; если нужен ответ — подготовь его через propose_message.',
        })
        toast(`${card.key} снова в работе`)
      } catch (e) {
        toastError(e, 'Не удалось вернуть задачу в работу')
      }
    })
  const meta = (
    <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 text-[12px] text-muted">
      <span className="tnum font-mono text-[11.5px] text-subtle">{card.key}</span>
      {who ? <span className="min-w-0 truncate">· {who}</span> : null}
      <span title={card.closed_at ?? undefined}>· готово {timeAgo(card.closed_at, now)}</span>
    </div>
  )

  if (compact) {
    return (
      <div className="rounded-card border border-line bg-surface px-3.5 py-2.5">
        {meta}
        <TaskLink id={card.id} className="mt-0.5 block truncate text-[13.5px] font-medium text-fg hover:underline">
          {card.title}
        </TaskLink>
        {text ? <p className="mt-0.5 line-clamp-2 text-[12.5px] text-muted [overflow-wrap:anywhere]">{text}</p> : null}
      </div>
    )
  }

  return (
    <article className={cx('rounded-card border border-line bg-surface px-4 py-3.5 shadow-card', onSeen && 'shadow-[inset_3px_0_0_var(--orbis-good)]')}>
      {meta}
      <TaskLink id={card.id} className="mt-1 block text-[15px] leading-snug font-semibold text-fg [overflow-wrap:anywhere] hover:underline">
        {card.title}
      </TaskLink>
      {text ? (
        <p className="mt-2 text-[14px] leading-relaxed text-fg-2 [overflow-wrap:anywhere]">{text}</p>
      ) : (
        <p className="mt-2 text-[13px] text-muted">Агент не оставил итога — посмотрите подробности в задаче.</p>
      )}
      {text && !brief ? <p className="mt-1 text-[12px] text-subtle">Краткого итога нет — показано начало подробного отчёта.</p> : null}
      {news ? (
        <div className="mt-2.5 rounded-ctl border border-line bg-warn-soft px-3 py-2 text-[13px]">
          <div className="font-medium text-fg">
            После закрытия в Битрикс24 {news.count === 1 ? 'новое сообщение' : `новых сообщений: ${news.count}`}
          </div>
          <p className="mt-0.5 line-clamp-3 whitespace-pre-wrap text-fg-2 [overflow-wrap:anywhere]">
            {news.author}: {news.text}
          </p>
        </div>
      ) : null}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" variant="primary" onClick={() => openTask(card.id)}>
          Открыть задачу
        </Button>
        {news && card.assignee_agent_id ? (
          <Button size="sm" icon="refresh" disabled={busy} onClick={() => void handBack()}>
            Отдать агенту
          </Button>
        ) : null}
        {onSeen ? (
          <Button size="sm" variant="ghost" icon="check" onClick={onSeen}>
            Просмотрено
          </Button>
        ) : null}
      </div>
    </article>
  )
}
